import type { ReactNode } from 'react';

import { FieldError } from '@nesso/ui/primitives/field';
import { RadioGroup } from '@nesso/ui/primitives/radio-group';
import { RadioGroupItemField } from '@nesso/ui/primitives/radio-group-item-field';

import { type PaymentMethod, paymentMethodOptions } from '@/entities/payment';

type Method = Exclude<PaymentMethod, 'CARD_ON_FILE'>;

type Props = {
    value: Method | undefined;
    error?: string;
    disabled?: boolean;
    onValueChange: (method: Method) => void;
    children: ReactNode;
};

// The payment side's External card description doesn't fit a refund; only this sheet's copy changes.
const refundMethodOptions = paymentMethodOptions.map((option) =>
    option.value === 'EXTERNAL_CARD'
        ? { ...option, description: 'Record a refund from external processor' }
        : option,
);

export function RefundPaymentSheetMethod({
    value,
    error,
    disabled,
    onValueChange,
    children,
}: Props) {
    return (
        <section className="flex flex-col gap-4">
            <div className="flex h-9 items-center">
                <h3 className="text-lg font-bold">Select refund method</h3>
            </div>
            <RadioGroup<Method>
                aria-label="Select refund method"
                value={value ?? null}
                onValueChange={onValueChange}
            >
                {refundMethodOptions.map((option) => (
                    <div key={option.value}>
                        <RadioGroupItemField
                            id={`refund-payment-method-${option.value.toLowerCase()}`}
                            value={option.value}
                            label={option.label}
                            description={option.description}
                            disabled={disabled || option.disabled}
                        />
                        {value === option.value && <div className="px-6 pt-2 pb-6">{children}</div>}
                    </div>
                ))}
            </RadioGroup>
            {error && <FieldError>{error}</FieldError>}
        </section>
    );
}
